import {inject, Injectable} from '@angular/core';
import {RdsToastService} from "@rds/angular-components";
import {RdsToastGlobalConfig} from "@rds/angular-components";
import {Observable, of} from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";

const DEFAULT_TOAST_OPTIONS: Partial<RdsToastGlobalConfig> = {
  maxOpened: 3,
  closeButton: true,
  newestOnTop: false,
  tapToDismiss: true,
  extendedTimeOut: 0,
}

export const TOAST_OPTIONS: { [key: string]: Partial<RdsToastGlobalConfig> } = {
  default: {
    ...DEFAULT_TOAST_OPTIONS,
    disableTimeOut: false,
    timeOut: 0,
  },
  success: {
    ...DEFAULT_TOAST_OPTIONS,
    disableTimeOut: false,
    timeOut: 5000,
  },
  info: {
    ...DEFAULT_TOAST_OPTIONS,
    disableTimeOut: false,
    timeOut: 5000,
  },
  warning: {
    ...DEFAULT_TOAST_OPTIONS,
    disableTimeOut: false,
    timeOut: 5000,
  },
  critical_warning: {
    ...DEFAULT_TOAST_OPTIONS,
    disableTimeOut: false,
    timeOut: 0,
  },
  error: {
    ...DEFAULT_TOAST_OPTIONS,
    disableTimeOut: false,
    timeOut: 0,
  }
}

@Injectable({
  providedIn: 'root'
})
export class FeedbackService {
  private readonly rdsToastService = inject(RdsToastService);

  emitWarning(warningText: string) {
    this.showToast(() => this.rdsToastService.warning(warningText, "", TOAST_OPTIONS['warning']));
  }

  emitCriticalWarning(warningText: string) {
    this.showToast(() => this.rdsToastService.warning(warningText, "", TOAST_OPTIONS['critical_warning']));
  }

  emitError(errorText: string) {
    this.showToast(() => this.rdsToastService.error(errorText, "", TOAST_OPTIONS['error']));
  }

  emitInfo(infoText: string) {
    this.showToast(() => this.rdsToastService.info(infoText, "", TOAST_OPTIONS['info']));
  }

  emitSuccess(successText: string) {
    this.showToast(() => this.rdsToastService.success(successText, "", TOAST_OPTIONS['success']));
  }

  // Guard against NG0205 when the injector is destroyed (e.g., during teardown
  // or when async HTTP errors arrive after navigation away from a view).
  private showToast(fn: () => void) {
    try {
      fn();
    } catch (e: any) {
      if (e?.code !== 'NG0205' && !e?.message?.includes('NG0205')) {
        throw e;
      }
    }
  }

  private notifyServerError(operation: string) {
    this.emitError(`Server was unable to answer ${operation}`)
  }

  private notifyUnknownError() {
    this.emitError("An unknown error occurred! Please reload.")
  }

  warnUserWhenOld(operation = 'operation', isOld: boolean) {
    if (isOld) {
      this.emitCriticalWarning(operation + " returned old data!")
    }
  }

  handleError<T>(operation = 'operation', fallback?: T) {
    return (error: any): Observable<T> => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 404 || error.status === 500) {
          this.notifyServerError(operation);
        } else {
          this.notifyUnknownError();
        }
      } else {
        this.notifyUnknownError();
      }
      console.error(`${operation} failed${fallback ? " with fallback " : ""}: ${error.message}`, fallback);
      return of(fallback as T);
    }
  }
}
